import { 
  GEMINI_CONFIG, 
  ANALYSIS_PROMPT, 
  GeminiAnalysisRequest, 
  GeminiAnalysisResponse 
} from '../config/gemini';

// Manual chart selection interface
interface ChartSelection {
  x: number;
  y: number;
  width: number;
  height: number;
}

class AIAnalysisService {
  private selectionOverlay: HTMLDivElement | null = null;
  private isSelecting: boolean = false;
  private selectionStart: { x: number; y: number } | null = null;

  // Manual chart area selection with mobile support
  async selectChartArea(): Promise<string | null> {
    return new Promise((resolve) => {
      console.log('Starting chart area selection...');
      
      // Enhanced mobile detection
      const isMobile = this.detectMobileDevice();
      
      if (isMobile) {
        console.log('📱 Mobile device detected - using enhanced touch-friendly selection');
        // Show mobile-specific guidance
        this.showMobileGuidance();
      } else {
        console.log('🖥️ Desktop device detected - using mouse selection');
      }
      
      // Create selection overlay
      this.createSelectionOverlay(isMobile);
      
      // Set up selection handlers for both mobile and desktop
      this.setupSelectionHandlers((selectedArea) => {
        if (selectedArea) {
          this.captureSelectedArea(selectedArea, isMobile).then(resolve);
        } else {
          resolve(null);
        }
        this.removeSelectionOverlay();
      }, isMobile);
    });
  }

  private detectMobileDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'iemobile', 'opera mini'];
    const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));
    
    // Multiple detection methods for better accuracy
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768;
    const isPortrait = window.innerHeight > window.innerWidth;
    
    return isMobileUA || (hasTouch && (isSmallScreen || isPortrait));
  }

  private showMobileGuidance(): void {
    // Add specific mobile guidance
    console.log(`
📱 MOBILE CHART CAPTURE TIPS:
1. For best results, use your device's built-in screenshot feature
2. Screenshots: iOS (Power + Volume Up), Android (Power + Volume Down)  
3. After taking screenshot, you can upload it manually if needed
4. Alternatively, try the selection tool but results may vary on mobile browsers
    `);
  }

  private createSelectionOverlay(isMobile: boolean = false): void {
    // Remove existing overlay if any
    this.removeSelectionOverlay();

    // Create overlay element
    this.selectionOverlay = document.createElement('div');
    this.selectionOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.3);
      cursor: ${isMobile ? 'pointer' : 'crosshair'};
      z-index: 10000;
      user-select: none;
      touch-action: ${isMobile ? 'pan-x pan-y' : 'none'};
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    `;

    // Add instructions with better mobile optimization
    const instructions = document.createElement('div');
    instructions.style.cssText = `
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: ${isMobile ? '20px' : '15px 25px'};
      border-radius: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      font-size: ${isMobile ? '16px' : '16px'};
      text-align: center;
      max-width: ${isMobile ? '90%' : '400px'};
      z-index: 10001;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 215, 0, 0.3);
    `;
    
    if (isMobile) {
      instructions.innerHTML = `
        <div style="margin-bottom: 10px;">📊 <strong>Select Chart Area</strong></div>
        <div style="margin-bottom: 8px;">Touch and drag to select the chart area</div>
        <div style="font-size: 14px; color: #ffd700;">💡 Tip: For best results, use device screenshot instead</div>
        <div style="font-size: 12px; margin-top: 8px; color: #ccc;">Tap outside to cancel</div>
        <div style="margin-top: 10px;">
          <button id="mobile-screenshot-tip" style="
            background: #ffd700;
            color: #000;
            border: none;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: bold;
            cursor: pointer;
          ">Show Screenshot Tips</button>
        </div>
      `;
    } else {
      instructions.innerHTML = `
        <strong>📊 Select Chart Area</strong><br>
        Drag to select the TradingView chart area you want to analyze.<br>
        <small>Press ESC to cancel</small>
      `;
    }

    // Add screenshot tips button handler for mobile
    if (isMobile) {
      instructions.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.id === 'mobile-screenshot-tip') {
          e.stopPropagation();
          this.showMobileScreenshotTips();
        }
      });
    }

    this.selectionOverlay.appendChild(instructions);
    document.body.appendChild(this.selectionOverlay);
  }

  private showMobileScreenshotTips(): void {
    const tipModal = document.createElement('div');
    tipModal.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.95);
      color: white;
      padding: 25px;
      border-radius: 15px;
      max-width: 90%;
      z-index: 10002;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      border: 2px solid #ffd700;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    `;
    
    tipModal.innerHTML = `
      <div style="text-align: center; margin-bottom: 20px;">
        <div style="font-size: 24px; margin-bottom: 10px;">📱</div>
        <h3 style="margin: 0; color: #ffd700;">Mobile Screenshot Guide</h3>
      </div>
      <div style="text-align: left; line-height: 1.6;">
        <p><strong>📱 iPhone/iPad:</strong><br>Press Volume Up + Power button simultaneously</p>
        <p><strong>🤖 Android:</strong><br>Press Volume Down + Power button simultaneously</p>
        <p><strong>✨ Why screenshots work better:</strong><br>• Captures actual chart content<br>• No browser limitations<br>• Better image quality</p>
      </div>
      <div style="text-align: center; margin-top: 20px;">
        <button id="close-tips" style="
          background: #ffd700;
          color: #000;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: bold;
          cursor: pointer;
          font-size: 16px;
        ">Got it!</button>
      </div>
    `;
    
    const safeRemove = () => {
      try {
        if (tipModal.parentNode && document.body.contains(tipModal)) {
          document.body.removeChild(tipModal);
        }
      } catch (error) {
        console.warn('Error removing tip modal:', error);
        // Try alternative removal method
        try {
          tipModal.remove();
        } catch (e) {
          console.warn('Alternative removal also failed:', e);
        }
      }
    };
    
    tipModal.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.id === 'close-tips' || target === tipModal) {
        safeRemove();
      }
    });
    
    try {
      document.body.appendChild(tipModal);
    } catch (error) {
      console.error('Error appending tip modal:', error);
    }
  }

  private setupSelectionHandlers(onComplete: (selection: ChartSelection | null) => void, isMobile: boolean = false): void {
    if (!this.selectionOverlay) return;

    let selectionBox: HTMLDivElement | null = null;
    let touchStartTime = 0;
    let lastTouchTime = 0;

    // Enhanced mobile touch handlers
    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Only handle single touch for selection
      if (e.touches.length !== 1) return;
      
      const touch = e.touches[0];
      if (!touch) return;
      
      // Check if touch started on overlay (not on instructions or buttons)
      const target = e.target as HTMLElement;
      if (target !== this.selectionOverlay && !this.selectionOverlay?.contains(target)) return;
      if (target.tagName === 'BUTTON' || target.closest('button')) return;
      
      touchStartTime = Date.now();
      this.isSelecting = true;
      this.selectionStart = { x: touch.clientX, y: touch.clientY };

      // Create selection box with enhanced mobile styling
      selectionBox = document.createElement('div');
      selectionBox.style.cssText = `
        position: fixed;
        border: 3px dashed #ffeb3b;
        background: rgba(255, 235, 59, 0.2);
        pointer-events: none;
        z-index: 10001;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(255, 235, 59, 0.3);
        backdrop-filter: blur(2px);
      `;
      document.body.appendChild(selectionBox);
      
      // Add haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (!this.isSelecting || !this.selectionStart || !selectionBox) return;
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      if (!touch) return;

      // Throttle touch move events for better performance
      const now = Date.now();
      if (now - lastTouchTime < 16) return; // ~60fps
      lastTouchTime = now;

      const currentX = touch.clientX;
      const currentY = touch.clientY;
      const startX = this.selectionStart.x;
      const startY = this.selectionStart.y;

      const left = Math.min(startX, currentX);
      const top = Math.min(startY, currentY);
      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - startY);

      // Update selection box with smooth transitions
      selectionBox.style.left = `${left}px`;
      selectionBox.style.top = `${top}px`;
      selectionBox.style.width = `${width}px`;
      selectionBox.style.height = `${height}px`;
      
      // Add visual feedback based on selection size
      const minSize = 80;
      const isValidSize = width >= minSize && height >= minSize;
      selectionBox.style.borderColor = isValidSize ? '#4caf50' : '#ffeb3b';
      selectionBox.style.backgroundColor = isValidSize ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 235, 59, 0.2)';
    };

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (!this.isSelecting || !this.selectionStart) return;

      this.isSelecting = false;
      
      // Check for minimum touch duration (prevent accidental taps)
      const touchDuration = Date.now() - touchStartTime;
      if (touchDuration < 100) {
        this.cleanupSelection(selectionBox);
        return;
      }
      
      const touch = e.changedTouches[0];
      if (!touch) {
        this.cleanupSelection(selectionBox);
        return;
      }

      const currentX = touch.clientX;
      const currentY = touch.clientY;
      const startX = this.selectionStart.x;
      const startY = this.selectionStart.y;

      const left = Math.min(startX, currentX);
      const top = Math.min(startY, currentY);
      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - startY);

      // Clean up selection box
      this.cleanupSelection(selectionBox);

      // Validate selection size (smaller minimum for mobile)
      const minSize = 80;
      if (width < minSize || height < minSize) {
        this.showMobileSelectionTip(minSize);
        return;
      }

      // Add success haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([50, 50, 50]);
      }

      // Return selection
      onComplete({
        x: left,
        y: top,
        width,
        height
      });
    };

    // Handle touch cancel events
    const handleTouchCancel = (e: TouchEvent) => {
      this.isSelecting = false;
      this.cleanupSelection(selectionBox);
    };

    // Desktop mouse handlers
    const handleMouseDown = (e: MouseEvent) => {
      if (e.target !== this.selectionOverlay) return;
      
      this.isSelecting = true;
      this.selectionStart = { x: e.clientX, y: e.clientY };

      // Create selection box
      selectionBox = document.createElement('div');
      selectionBox.style.cssText = `
        position: fixed;
        border: 2px dashed #ffeb3b;
        background: rgba(255, 235, 59, 0.1);
        pointer-events: none;
        z-index: 10001;
      `;
      document.body.appendChild(selectionBox);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!this.isSelecting || !this.selectionStart || !selectionBox) return;

      const currentX = e.clientX;
      const currentY = e.clientY;
      const startX = this.selectionStart.x;
      const startY = this.selectionStart.y;

      const left = Math.min(startX, currentX);
      const top = Math.min(startY, currentY);
      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - startY);

      selectionBox.style.left = `${left}px`;
      selectionBox.style.top = `${top}px`;
      selectionBox.style.width = `${width}px`;
      selectionBox.style.height = `${height}px`;
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!this.isSelecting || !this.selectionStart) return;

      this.isSelecting = false;
      
      const currentX = e.clientX;
      const currentY = e.clientY;
      const startX = this.selectionStart.x;
      const startY = this.selectionStart.y;

      const left = Math.min(startX, currentX);
      const top = Math.min(startY, currentY);
      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - startY);

      // Clean up selection box
      if (selectionBox) {
        selectionBox.remove();
        selectionBox = null;
      }

      // Validate selection size
      if (width < 100 || height < 100) {
        alert('Selection too small. Please select a larger area of the chart.');
        return;
      }

      // Return selection
      onComplete({
        x: left,
        y: top,
        width,
        height
      });
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectionBox) {
          selectionBox.remove();
        }
        onComplete(null);
      }
    };

    // Add appropriate event listeners based on device type
    if (isMobile) {
      // Touch events for mobile
      this.selectionOverlay.addEventListener('touchstart', handleTouchStart, { passive: false });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd, { passive: false });
      
      // Tap outside to cancel
      this.selectionOverlay.addEventListener('touchstart', (e) => {
        if (e.target === this.selectionOverlay && !this.isSelecting) {
          onComplete(null);
        }
      });
    } else {
      // Mouse events for desktop
      this.selectionOverlay.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('keydown', handleKeyDown);
    }

    // Store cleanup function
    (this.selectionOverlay as any).cleanup = () => {
      if (isMobile) {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      } else {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('keydown', handleKeyDown);
      }
      if (selectionBox) {
        selectionBox.remove();
      }
    };
  }

  private removeSelectionOverlay(): void {
    if (this.selectionOverlay) {
      // Call cleanup function if it exists
      if ((this.selectionOverlay as any).cleanup) {
        try {
          (this.selectionOverlay as any).cleanup();
        } catch (error) {
          console.warn('Error during overlay cleanup:', error);
        }
      }
      
      // Safe removal with try-catch
      try {
        if (this.selectionOverlay.parentNode && document.body.contains(this.selectionOverlay)) {
          this.selectionOverlay.remove();
        }
      } catch (error) {
        console.warn('Error removing selection overlay:', error);
      }
      
      this.selectionOverlay = null;
    }
    this.isSelecting = false;
    this.selectionStart = null;
  }

  private async captureSelectedArea(selection: ChartSelection, isMobile: boolean = false): Promise<string | null> {
    try {
      console.log('Capturing selected area:', selection);

      // Add debug visualization
      this.showDebugInfo(selection);

      // Method 1: Try native browser screen capture API (most reliable for TradingView)
      let capturedImage = await this.tryScreenCaptureAPI(selection, isMobile);
      
      if (!capturedImage) {
        console.log('Screen capture failed, trying html2canvas...');
        
        // Method 2: Try capturing with html2canvas
        capturedImage = await this.tryHtml2CanvasCapture(selection, isMobile);
      }
      
      if (!capturedImage) {
        console.log('html2canvas failed, trying DOM screenshot...');
        
        // Method 3: Try capturing visible DOM elements
        capturedImage = await this.tryDOMCapture(selection, isMobile);
      }

      if (!capturedImage) {
        // Show helpful error message
        alert('Chart capture failed. Tips:\n\n' +
              '1. Make sure to select directly over the TradingView chart\n' +
              '2. Wait for the chart to fully load before selecting\n' +
              '3. Try selecting a larger area that includes chart elements\n' +
              '4. Check browser console for detailed error messages');
        throw new Error('All capture methods failed to get chart content');
      }

      console.log('Successfully captured chart area with content');
      return capturedImage;

    } catch (error) {
      console.error('Failed to capture selected area:', error);
      return null;
    }
  }

  private showDebugInfo(selection: ChartSelection): void {
    const elementsInArea = this.findElementsInArea(selection);
    
    console.log('Debug Info for Selection:', {
      selection,
      elementsFound: elementsInArea.length,
      elements: elementsInArea.map(el => ({
        tag: el.tagName,
        id: el.id,
        className: el.className,
        rect: el.getBoundingClientRect()
      }))
    });

    // Look specifically for TradingView elements
    const tradingViewElements = elementsInArea.filter(el => 
      el.tagName === 'IFRAME' || 
      el.className.includes('tradingview') ||
      el.id.includes('tradingview') ||
      el.className.includes('tv-') ||
      el.getAttribute('src')?.includes('tradingview')
    );

    console.log('TradingView elements in selection:', tradingViewElements);

    if (tradingViewElements.length === 0) {
      console.warn('⚠️ No TradingView elements detected in selection. Make sure to select over the chart area.');
    }
  }

  private async tryHtml2CanvasCapture(selection: ChartSelection, isMobile: boolean = false): Promise<string | null> {
    try {
      console.log('Trying html2canvas capture...');
      
      const html2canvas = await import('html2canvas');
      
      // Enhanced options for better mobile capture
      const captureOptions = {
        useCORS: true,
        allowTaint: true,
        logging: false, // Disable logging on mobile for performance
        width: isMobile ? Math.min(window.innerWidth, 1200) : window.innerWidth,
        height: isMobile ? Math.min(window.innerHeight, 1200) : window.innerHeight,
        scale: isMobile ? Math.min(window.devicePixelRatio, 2) : window.devicePixelRatio, // Limit scale on mobile
        backgroundColor: '#000000',
        removeContainer: true,
        foreignObjectRendering: false, // Better mobile compatibility
        imageTimeout: isMobile ? 5000 : 15000, // Shorter timeout on mobile
        onclone: isMobile ? (clonedDoc: Document) => {
          // Mobile-specific optimizations for cloned document
          const clonedBody = clonedDoc.body;
          if (clonedBody) {
            // Remove complex animations that can cause issues on mobile
            const style = clonedDoc.createElement('style');
            style.textContent = `
              * { 
                animation: none !important; 
                transition: none !important;
                transform: none !important;
              }
            `;
            clonedDoc.head.appendChild(style);
          }
        } : undefined
      };
      
      console.log(isMobile ? '📱 Using mobile-optimized html2canvas settings...' : '🖥️ Using desktop html2canvas settings...');
      
      const canvas = await html2canvas.default(document.body, captureOptions);

      console.log('Full page captured, extracting selection...');

      // Create canvas for selected area
      const selectedCanvas = document.createElement('canvas');
      selectedCanvas.width = selection.width;
      selectedCanvas.height = selection.height;
      const ctx = selectedCanvas.getContext('2d');

      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      // Draw the selected portion
      ctx.drawImage(
        canvas,
        selection.x, selection.y, selection.width, selection.height,
        0, 0, selection.width, selection.height
      );

      // Enhanced content validation
      const hasContent = this.validateImageContent(selectedCanvas);
      if (!hasContent) {
        console.log('html2canvas captured empty content');
        return null;
      }

      const base64Image = selectedCanvas.toDataURL('image/png', isMobile ? 0.8 : 0.95); // Lower quality on mobile to reduce size
      
      // Additional validation - check image size
      if (base64Image.length < 1000) {
        console.log('Image too small, likely empty');
        return null;
      }

      console.log(`html2canvas capture successful (${isMobile ? 'mobile' : 'desktop'} mode), image size:`, base64Image.length);
      return base64Image.split(',')[1];

    } catch (error) {
      console.error('html2canvas capture failed:', error);
      if (isMobile) {
        console.log('📱 Mobile html2canvas failed - this is common on mobile browsers');
        console.log('💡 Try using device screenshot instead for better results');
      }
      return null;
    }
  }

  private async tryScreenCaptureAPI(selection: ChartSelection, isMobile: boolean = false): Promise<string | null> {
    try {
      console.log('Trying native screen capture API...');
      
      // Check if screen capture API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        console.log('Screen capture API not available');
        return null;
      }

      if (isMobile) {
        console.log('📱 Screen capture on mobile - very limited browser support');
        console.log('💡 Most mobile browsers do not support getDisplayMedia API');
        console.log('🔄 Falling back to alternative capture methods...');
        return null; // Skip screen capture API on mobile as it's rarely supported
      } else {
        console.log('📺 Chrome will ask to share your screen - this is the best way to capture TradingView charts!');
        console.log('💡 Tip: Select "This Tab" for best results when the sharing dialog appears');
      }
      
      // Request screen capture with preference for current tab
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'never'
        },
        audio: false
      } as any);

      console.log('✅ Screen sharing granted, capturing selected area...');

      // Create video element to capture frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      return new Promise((resolve) => {
        video.addEventListener('loadedmetadata', () => {
          // Create canvas for capture
          const canvas = document.createElement('canvas');
          canvas.width = selection.width;
          canvas.height = selection.height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            stream.getTracks().forEach(track => track.stop());
            resolve(null);
            return;
          }

          // Calculate scaling factors
          const scaleX = video.videoWidth / window.innerWidth;
          const scaleY = video.videoHeight / window.innerHeight;

          // Draw the selected area from the video
          ctx.drawImage(
            video,
            selection.x * scaleX, selection.y * scaleY,
            selection.width * scaleX, selection.height * scaleY,
            0, 0, selection.width, selection.height
          );

          // Stop the stream
          stream.getTracks().forEach(track => track.stop());
          console.log('📸 Screen capture completed, validating content...');

          // Validate content
          const hasContent = this.validateImageContent(canvas);
          if (!hasContent) {
            console.log('Screen capture API captured empty content');
            resolve(null);
            return;
          }

          const base64Image = canvas.toDataURL('image/png');
          console.log('🎯 Screen capture API successful! Image size:', base64Image.length, 'bytes');
          resolve(base64Image.split(',')[1]);
        });
      });

    } catch (error) {
      if (error instanceof Error && error.name === 'NotAllowedError') {
        console.log('❌ Screen sharing was denied by user');
      } else {
        console.error('Screen capture API failed:', error);
      }
      return null;
    }
  }

  private async tryDOMCapture(selection: ChartSelection, isMobile: boolean = false): Promise<string | null> {
    try {
      console.log('Trying DOM-based capture...');
      
      // Find elements in the selected area
      const elementsInArea = this.findElementsInArea(selection);
      
      if (elementsInArea.length === 0) {
        console.log('No elements found in selected area');
        return null;
      }

      console.log('Found elements in area:', elementsInArea.length);

      // Try to capture the most relevant element (likely iframe or chart container)
      for (const element of elementsInArea) {
        try {
          const html2canvas = await import('html2canvas');
          
          const canvas = await html2canvas.default(element as HTMLElement, {
            useCORS: true,
            allowTaint: true,
            logging: true,
            width: selection.width,
            height: selection.height
          });

          const hasContent = this.validateImageContent(canvas);
          if (hasContent) {
            const base64Image = canvas.toDataURL('image/png');
            console.log('DOM capture successful with element:', element.tagName, element.className);
            return base64Image.split(',')[1];
          }
        } catch (error) {
          console.log('Failed to capture element:', element.tagName, error);
          continue;
        }
      }

      // If individual elements failed, try creating a composite
      return this.createCompositeCapture(selection, elementsInArea, isMobile);

    } catch (error) {
      console.error('DOM capture failed:', error);
      return null;
    }
  }

  private findElementsInArea(selection: ChartSelection): Element[] {
    const elements: Element[] = [];
    
    // Get all elements and check if they intersect with selection
    const allElements = Array.from(document.querySelectorAll('*'));
    
    for (const element of allElements) {
      const rect = element.getBoundingClientRect();
      
      // Check if element intersects with selection
      if (this.rectanglesIntersect(selection, {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height
      })) {
        // Prioritize iframes, canvas, and chart-related elements
        if (element.tagName === 'IFRAME' || 
            element.tagName === 'CANVAS' ||
            element.className.includes('chart') ||
            element.className.includes('trading') ||
            element.id.includes('trading') ||
            element.id.includes('chart')) {
          elements.unshift(element); // Add to front
        } else {
          elements.push(element);
        }
      }
    }
    
    return elements.slice(0, 10); // Limit to 10 most relevant elements
  }

  private rectanglesIntersect(rect1: ChartSelection, rect2: ChartSelection): boolean {
    return !(rect1.x + rect1.width < rect2.x || 
             rect2.x + rect2.width < rect1.x || 
             rect1.y + rect1.height < rect2.y || 
             rect2.y + rect2.height < rect1.y);
  }

  private async createCompositeCapture(selection: ChartSelection, elements: Element[], isMobile: boolean = false): Promise<string | null> {
    try {
      console.log('Creating composite capture...');
      
      const canvas = document.createElement('canvas');
      canvas.width = selection.width;
      canvas.height = selection.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) return null;

      // Fill with background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add text indicating this is a fallback
      ctx.fillStyle = '#333333';
      ctx.font = isMobile ? '14px Arial' : '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Chart Analysis Area Selected', canvas.width / 2, 30);
      ctx.fillText(`${selection.width} x ${selection.height} pixels`, canvas.width / 2, 50);
      ctx.fillText(`${elements.length} elements detected`, canvas.width / 2, 70);
      
      // List detected elements
      elements.slice(0, 5).forEach((element, index) => {
        const text = `${element.tagName}${element.className ? '.' + element.className.split(' ')[0] : ''}`;
        ctx.fillText(text, canvas.width / 2, 100 + (index * 20));
      });

      if (isMobile) {
        ctx.fillText('Try selecting directly on the chart area', canvas.width / 2, canvas.height - 40);
        ctx.fillText('or use screenshot feature in browser', canvas.width / 2, canvas.height - 20);
      } else {
        ctx.fillText('Please try selecting directly on the chart area', canvas.width / 2, canvas.height - 30);
      }

      const base64Image = canvas.toDataURL('image/png');
      console.log('Composite capture created');
      return base64Image.split(',')[1];

    } catch (error) {
      console.error('Composite capture failed:', error);
      return null;
    }
  }

  private validateImageContent(canvas: HTMLCanvasElement): boolean {
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      
      let nonWhitePixels = 0;
      let totalPixels = pixels.length / 4;
      let colorVariation = 0;
      let lastColor = [0, 0, 0];
      
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const a = pixels[i + 3];
        
        // Count non-white, non-transparent pixels
        if (a > 0 && (r < 240 || g < 240 || b < 240)) {
          nonWhitePixels++;
        }
        
        // Check for color variation (indicates actual content)
        const colorDiff = Math.abs(r - lastColor[0]) + Math.abs(g - lastColor[1]) + Math.abs(b - lastColor[2]);
        if (colorDiff > 10) {
          colorVariation++;
        }
        lastColor = [r, g, b];
      }
      
      const contentRatio = nonWhitePixels / totalPixels;
      const variationRatio = colorVariation / totalPixels;
      
      console.log('Image validation:', {
        nonWhitePixels,
        totalPixels,
        contentRatio,
        colorVariation,
        variationRatio,
        hasContent: contentRatio > 0.1 && variationRatio > 0.05
      });
      
      // Image should have at least 10% non-white pixels and some color variation
      return contentRatio > 0.1 && variationRatio > 0.05;
      
    } catch (error) {
      console.error('Image validation failed:', error);
      return false;
    }
  }

  async analyzeChart(request: GeminiAnalysisRequest): Promise<GeminiAnalysisResponse> {
    if (!GEMINI_CONFIG.API_KEY) {
      throw new Error('Gemini API key not configured. Please add REACT_APP_GEMINI_API_KEY to your environment variables.');
    }

    try {
      // Show user guidance for screen capture
      console.log('Starting chart analysis with screen capture...');
      
      // Start manual chart selection
      console.log('Starting manual chart area selection...');
      const chartImage = await this.selectChartArea();
      
      if (!chartImage) {
        throw new Error('Chart selection was cancelled or failed. Please try again.');
      }

      console.log('Chart area selected, sending to AI for analysis...');

      return this.performAnalysisWithImage(request, chartImage);

    } catch (error) {
      console.error('Failed to analyze chart:', error);
      throw error;
    }
  }

  // New method for mobile screenshot uploads
  async analyzeChartWithImage(request: GeminiAnalysisRequest & { imageData: string | string[]; multipleImages?: boolean }): Promise<GeminiAnalysisResponse> {
    if (!GEMINI_CONFIG.API_KEY) {
      throw new Error('Gemini API key not configured. Please add REACT_APP_GEMINI_API_KEY to your environment variables.');
    }

    try {
      console.log('📱 Starting chart analysis with uploaded image(s)...');
      
      if (!request.imageData) {
        throw new Error('No image data provided for analysis.');
      }

      // Handle both single and multiple images
      if (Array.isArray(request.imageData)) {
        console.log(`📸 Processing ${request.imageData.length} images for AI analysis...`);
        return this.performAnalysisWithMultipleImages(request, request.imageData);
      } else {
        console.log('Processing single uploaded image for AI analysis...');
        return this.performAnalysisWithImage(request, request.imageData);
      }

    } catch (error) {
      console.error('Failed to analyze uploaded image(s):', error);
      throw error;
    }
  }

  // Shared method for performing analysis with image data
  private async performAnalysisWithImage(request: GeminiAnalysisRequest, imageData: string): Promise<GeminiAnalysisResponse> {
    // Create analysis prompt for visual analysis
    const analysisPrompt = this.createVisualAnalysisPrompt(request);

    // Try Gemini API with the captured image
    const attempts = [
      { url: GEMINI_CONFIG.API_URL, model: GEMINI_CONFIG.MODEL },
      { url: GEMINI_CONFIG.FALLBACK_URL, model: GEMINI_CONFIG.FALLBACK_MODEL }
    ];

    for (const attempt of attempts) {
      try {
        const response = await fetch(`${attempt.url}?key=${GEMINI_CONFIG.API_KEY}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [
                {
                  text: analysisPrompt
                },
                {
                  inlineData: {
                    mimeType: "image/png",
                    data: imageData
                  }
                }
              ]
            }],
            generationConfig: {
              temperature: GEMINI_CONFIG.TEMPERATURE,
              maxOutputTokens: GEMINI_CONFIG.MAX_TOKENS,
            }
          })
        });

        if (!response.ok) {
          if (response.status === 404 && attempts.indexOf(attempt) < attempts.length - 1) {
            console.warn(`Model ${attempt.model} not found, trying fallback...`);
            continue;
          }
          throw new Error(`Gemini API error: ${response.status}`);
        }

        const data = await response.json();
        
        if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
          throw new Error(`Invalid response from Gemini API`);
        }

        const analysisText = data.candidates[0].content.parts[0].text;
        
        try {
          // Parse JSON response
          let cleanedText = analysisText.trim();
          if (cleanedText.startsWith('```json')) {
            cleanedText = cleanedText.replace(/^```json\s*/, '').replace(/```\s*$/, '');
          }
          
          const jsonStart = cleanedText.indexOf('{');
          const jsonEnd = cleanedText.lastIndexOf('}');
          if (jsonStart !== -1 && jsonEnd !== -1) {
            cleanedText = cleanedText.substring(jsonStart, jsonEnd + 1);
          }
          
          const analysisResult = JSON.parse(cleanedText);
          return this.validateAnalysisResponse(analysisResult);
        } catch (parseError) {
          console.error('Failed to parse JSON, creating structured response from text');
          return this.createVisualAnalysisFallback(request, analysisText);
        }
      } catch (error) {
        console.error(`Analysis attempt failed:`, error);
        if (attempts.indexOf(attempt) === attempts.length - 1) {
          throw error;
        }
      }
    }

    throw new Error('All analysis attempts failed');
  }

  // Method for handling multiple images analysis
  private async performAnalysisWithMultipleImages(request: GeminiAnalysisRequest, imageDataArray: string[]): Promise<GeminiAnalysisResponse> {
    // Create analysis prompt for multiple images
    const analysisPrompt = this.createMultipleImagesAnalysisPrompt(request, imageDataArray.length);

    // Try Gemini API with multiple images
    const attempts = [
      { url: GEMINI_CONFIG.API_URL, model: GEMINI_CONFIG.MODEL },
      { url: GEMINI_CONFIG.FALLBACK_URL, model: GEMINI_CONFIG.FALLBACK_MODEL }
    ];

    for (const attempt of attempts) {
      try {
        // Build parts array with text prompt and all images
        const parts = [
          {
            text: analysisPrompt
          },
          ...imageDataArray.map((imageData, index) => ({
            inlineData: {
              mimeType: "image/png",
              data: imageData
            }
          }))
        ];

        const response = await fetch(`${attempt.url}?key=${GEMINI_CONFIG.API_KEY}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: parts
            }],
            generationConfig: {
              temperature: GEMINI_CONFIG.TEMPERATURE,
              maxOutputTokens: GEMINI_CONFIG.MAX_TOKENS,
            }
          })
        });

        if (!response.ok) {
          if (response.status === 404 && attempts.indexOf(attempt) < attempts.length - 1) {
            console.warn(`Model ${attempt.model} not found, trying fallback...`);
            continue;
          }
          throw new Error(`Gemini API error: ${response.status}`);
        }

        const data = await response.json();
        
        if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
          throw new Error(`Invalid response from Gemini API`);
        }

        const analysisText = data.candidates[0].content.parts[0].text;
        
        try {
          // Parse JSON response
          let cleanedText = analysisText.trim();
          if (cleanedText.startsWith('```json')) {
            cleanedText = cleanedText.replace(/^```json\s*/, '').replace(/```\s*$/, '');
          }
          
          const jsonStart = cleanedText.indexOf('{');
          const jsonEnd = cleanedText.lastIndexOf('}');
          if (jsonStart !== -1 && jsonEnd !== -1) {
            cleanedText = cleanedText.substring(jsonStart, jsonEnd + 1);
          }
          
          const analysisResult = JSON.parse(cleanedText);
          return this.validateAnalysisResponse(analysisResult);
        } catch (parseError) {
          console.error('Failed to parse JSON, creating structured response from text');
          return this.createVisualAnalysisFallback(request, analysisText);
        }
      } catch (error) {
        console.error(`Multiple images analysis attempt failed:`, error);
        if (attempts.indexOf(attempt) === attempts.length - 1) {
          throw error;
        }
      }
    }

    throw new Error('All multiple images analysis attempts failed');
  }

  private createMultipleImagesAnalysisPrompt(request: GeminiAnalysisRequest, imageCount: number): string {
    return `
COMPREHENSIVE TRADING ANALYSIS - MULTIPLE CHART IMAGES + FUNDAMENTAL

You are an expert financial trader, technical analyst, and fundamental analyst analyzing ${imageCount} chart images for ${request.instrument} on ${request.timeframe} timeframe.

PLEASE ANALYZE ALL ${imageCount} IMAGES PROVIDED:
- These images may show different views, timeframes, or aspects of the same instrument
- Look for consistency or divergence between the charts
- Combine insights from all images for a comprehensive analysis

TECHNICAL ANALYSIS REQUIREMENTS (from all chart images):
1. Read current prices from all chart displays and note any differences
2. Identify trend direction across all timeframes/views shown
3. Analyze visible technical indicators (RSI, MACD, moving averages, etc.) in all images
4. Look for support and resistance levels visible across charts
5. Identify chart patterns (triangles, flags, channels, head & shoulders, etc.)
6. Examine volume if visible in any chart
7. Note significant price levels, round numbers, or psychological levels
8. Compare and contrast signals between different chart views
9. Assess overall market sentiment from the combined visual data

FUNDAMENTAL ANALYSIS REQUIREMENTS (from your knowledge):
1. Current economic factors affecting ${request.instrument}
2. Central bank policies and interest rate environment
3. Geopolitical factors and market sentiment
4. Supply/demand fundamentals for this instrument
5. Recent economic events and upcoming catalysts
6. Macroeconomic context and outlook

CURRENT MARKET CONTEXT (December 2024):
- XAUUSD (Gold): Around $2300-$2450 range
- XAGUSD (Silver): Around $28-$35 range  
- BTCUSD: Around $90,000-$105,000 range
- ETHUSD: Around $3,200-$4,200 range
- Use realistic current market levels

REQUIRED JSON RESPONSE FORMAT:
{
  "sentiment": "bullish|bearish|neutral",
  "confidence": 1-100,
  "analysis": "Comprehensive technical analysis combining insights from all ${imageCount} chart images. Include observations about consistency between charts, different timeframe signals, and key patterns visible across the images.",
  "fundamentalAnalysis": {
    "economicFactors": "Current economic factors affecting this instrument (interest rates, inflation, economic data, etc.)",
    "marketDrivers": "Key fundamental drivers currently impacting price (central bank policies, geopolitical events, supply/demand, etc.)",
    "outlook": "Short to medium-term fundamental outlook based on upcoming events and economic conditions",
    "sentiment": "bullish|bearish|neutral",
    "strength": 1-100
  },
  "entryPoints": ["Specific entry levels based on combined chart analysis", "Alternative entry points from multiple timeframe analysis"],
  "exitPoints": ["Take profit levels from chart analysis", "Stop loss levels", "Additional exit strategies"],
  "riskLevel": "low|medium|high",
  "recommendation": "Trading recommendation combining both technical chart analysis from all images and fundamental factors",
  "keyLevels": {
    "support": ["Support levels visible across charts"],
    "resistance": ["Resistance levels visible across charts"]
  },
  "indicators": {
    "rsi": "RSI analysis from all charts showing indicators or general momentum assessment",
    "macd": "MACD analysis from all charts showing indicators or momentum analysis", 
    "bollinger": "Bollinger bands analysis from all charts showing indicators or volatility assessment"
  }
}

IMPORTANT: 
- Analyze ALL ${imageCount} images thoroughly
- Base technical analysis on what you can see across all chart images
- Look for confirmation or divergence between different chart views
- Use your knowledge for comprehensive fundamental analysis of ${request.instrument}
- Combine both perspectives for actionable trading insights
- Use actual price levels visible in the charts for technical recommendations
- If images show different timeframes, provide multi-timeframe analysis
`;
  }

  private createVisualAnalysisPrompt(request: GeminiAnalysisRequest): string {
    return `
COMPREHENSIVE TRADING ANALYSIS - VISUAL CHART + FUNDAMENTAL

You are an expert financial trader, technical analyst, and fundamental analyst analyzing a chart for ${request.instrument} on ${request.timeframe} timeframe.

Please provide BOTH technical analysis based on the chart image AND fundamental analysis based on your knowledge:

TECHNICAL ANALYSIS REQUIREMENTS (from chart):
1. Read the current price from the chart display
2. Identify trend direction from price action and candlestick patterns
3. Analyze any visible technical indicators (RSI, MACD, moving averages, etc.)
4. Look for support and resistance levels visible on the chart
5. Identify chart patterns (triangles, flags, channels, head & shoulders, etc.)
6. Examine volume if visible
7. Note any significant price levels, round numbers, or psychological levels
8. Assess overall market sentiment from the visual data

FUNDAMENTAL ANALYSIS REQUIREMENTS (from your knowledge):
1. Current economic factors affecting ${request.instrument}
2. Central bank policies and interest rate environment
3. Geopolitical factors and market sentiment
4. Supply/demand fundamentals for this instrument
5. Recent economic events and upcoming catalysts
6. Macroeconomic context and outlook

CURRENT MARKET CONTEXT (December 2024):
- XAUUSD (Gold): Around $2300-$2450 range
- XAGUSD (Silver): Around $28-$35 range  
- BTCUSD: Around $90,000-$105,000 range
- ETHUSD: Around $3,200-$4,200 range
- Use realistic current market levels

REQUIRED JSON RESPONSE FORMAT:
{
  "sentiment": "bullish|bearish|neutral",
  "confidence": 1-100,
  "analysis": "Detailed technical analysis based on visual chart inspection including current price, trend, patterns, and key observations from the actual chart",
  "fundamentalAnalysis": {
    "economicFactors": "Current economic factors affecting this instrument (interest rates, inflation, economic data, etc.)",
    "marketDrivers": "Key fundamental drivers currently impacting price (central bank policies, geopolitical events, supply/demand, etc.)",
    "outlook": "Short to medium-term fundamental outlook based on upcoming events and economic conditions",
    "sentiment": "bullish|bearish|neutral",
    "strength": 1-100
  },
  "entryPoints": ["Specific entry levels based on chart analysis", "Alternative entry points"],
  "exitPoints": ["Take profit levels from chart", "Stop loss levels", "Additional exit strategies"],
  "riskLevel": "low|medium|high",
  "recommendation": "Trading recommendation combining both technical chart analysis and fundamental factors",
  "keyLevels": {
    "support": ["Support levels visible on chart"],
    "resistance": ["Resistance levels visible on chart"]
  },
  "indicators": {
    "rsi": "RSI analysis if visible on chart or general momentum assessment",
    "macd": "MACD analysis if visible on chart or momentum analysis", 
    "bollinger": "Bollinger bands analysis if visible or volatility assessment"
  }
}

IMPORTANT: 
- Base technical analysis on what you can see in the chart image
- Use your knowledge for comprehensive fundamental analysis of ${request.instrument}
- Combine both perspectives for actionable trading insights
- Use actual price levels visible in the chart for technical recommendations
`;
  }

  private validateAnalysisResponse(response: any): GeminiAnalysisResponse {
    // Validate and ensure all required fields are present
    const validated: GeminiAnalysisResponse = {
      sentiment: ['bullish', 'bearish', 'neutral'].includes(response.sentiment) 
        ? response.sentiment 
        : 'neutral',
      confidence: typeof response.confidence === 'number' 
        ? Math.max(0, Math.min(100, response.confidence))
        : 50,
      analysis: typeof response.analysis === 'string' 
        ? response.analysis 
        : 'Visual chart analysis unavailable',
      fundamentalAnalysis: {
        economicFactors: typeof response.fundamentalAnalysis?.economicFactors === 'string'
          ? response.fundamentalAnalysis.economicFactors
          : 'Economic factors analysis unavailable at this time.',
        marketDrivers: typeof response.fundamentalAnalysis?.marketDrivers === 'string'
          ? response.fundamentalAnalysis.marketDrivers
          : 'Market drivers assessment pending additional analysis.',
        outlook: typeof response.fundamentalAnalysis?.outlook === 'string'
          ? response.fundamentalAnalysis.outlook
          : 'Fundamental outlook requires further evaluation.',
        sentiment: ['bullish', 'bearish', 'neutral'].includes(response.fundamentalAnalysis?.sentiment)
          ? response.fundamentalAnalysis.sentiment
          : 'neutral',
        strength: typeof response.fundamentalAnalysis?.strength === 'number'
          ? Math.max(0, Math.min(100, response.fundamentalAnalysis.strength))
          : 50
      },
      entryPoints: Array.isArray(response.entryPoints) 
        ? response.entryPoints 
        : ['Entry points to be determined from chart analysis'],
      exitPoints: Array.isArray(response.exitPoints) 
        ? response.exitPoints 
        : ['Exit strategy to be defined based on chart levels'],
      riskLevel: ['low', 'medium', 'high'].includes(response.riskLevel) 
        ? response.riskLevel 
        : 'medium',
      recommendation: typeof response.recommendation === 'string' 
        ? response.recommendation 
        : 'Review chart analysis and consult additional sources',
      keyLevels: {
        support: Array.isArray(response.keyLevels?.support) 
          ? response.keyLevels.support 
          : [],
        resistance: Array.isArray(response.keyLevels?.resistance) 
          ? response.keyLevels.resistance 
          : []
      },
      indicators: {
        rsi: typeof response.indicators?.rsi === 'string' 
          ? response.indicators.rsi 
          : 'RSI analysis from chart inspection',
        macd: typeof response.indicators?.macd === 'string' 
          ? response.indicators.macd 
          : 'MACD analysis from chart inspection',
        bollinger: typeof response.indicators?.bollinger === 'string' 
          ? response.indicators.bollinger 
          : 'Bollinger Bands analysis from chart inspection'
      }
    };

    return validated;
  }

  private createVisualAnalysisFallback(request: GeminiAnalysisRequest, rawText: string): GeminiAnalysisResponse {
    // Enhanced fallback for visual analysis
    const text = rawText.toLowerCase();
    
    // Try to extract sentiment from the AI's text response
    let sentiment: 'bullish' | 'bearish' | 'neutral' = 'neutral';
    let confidence = 65;
    
    if (text.includes('bullish') || text.includes('upward') || text.includes('buy') || text.includes('long')) {
      sentiment = 'bullish';
      confidence = 70;
    } else if (text.includes('bearish') || text.includes('downward') || text.includes('sell') || text.includes('short')) {
      sentiment = 'bearish';
      confidence = 70;
    }
    
    // Determine risk level from text
    let riskLevel: 'low' | 'medium' | 'high' = 'medium';
    if (text.includes('high risk') || text.includes('volatile') || text.includes('caution')) {
      riskLevel = 'high';
    } else if (text.includes('low risk') || text.includes('stable') || text.includes('safe')) {
      riskLevel = 'low';
    }
    
    return {
      sentiment,
      confidence,
      analysis: `Visual analysis of ${request.instrument} chart on ${request.timeframe} timeframe: ${rawText.substring(0, 800)}...`,
      fundamentalAnalysis: {
        economicFactors: `Economic factors for ${request.instrument} require additional analysis beyond chart inspection.`,
        marketDrivers: `Market drivers for ${request.instrument} should be evaluated through fundamental research.`,
        outlook: `Fundamental outlook for ${request.instrument} pending comprehensive economic analysis.`,
        sentiment: 'neutral',
        strength: 50
      },
      entryPoints: [
        'Chart analysis indicates potential entry opportunities',
        'Consult support/resistance levels visible on chart'
      ],
      exitPoints: [
        'Set stops based on chart structure',
        'Take profits at visible resistance zones',
        'Trail stops according to trend direction'
      ],
      riskLevel,
      recommendation: `Based on visual chart analysis, ${sentiment} bias suggested for ${request.instrument}. Review actual price levels and chart patterns before executing trades. Always use proper risk management.`,
      keyLevels: {
        support: ['Review chart for horizontal support levels'],
        resistance: ['Identify resistance zones from price action']
      },
      indicators: {
        rsi: 'Check RSI indicator if displayed on chart',
        macd: 'Observe MACD signals if visible on chart',
        bollinger: 'Analyze Bollinger Band levels if shown on chart'
      }
    };
  }

  private cleanupSelection(selectionBox: HTMLDivElement | null): void {
    if (selectionBox) {
      try {
        if (selectionBox.parentNode && document.body.contains(selectionBox)) {
          selectionBox.remove();
        }
      } catch (error) {
        console.warn('Error cleaning up selection box:', error);
        // Try alternative cleanup
        try {
          if (selectionBox.parentNode) {
            selectionBox.parentNode.removeChild(selectionBox);
          }
        } catch (e) {
          console.warn('Alternative cleanup also failed:', e);
        }
      }
    }
  }

  private showMobileSelectionTip(minSize: number): void {
    // Show mobile-friendly selection tip
    const tipElement = document.createElement('div');
    tipElement.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(255, 69, 0, 0.95);
      color: white;
      padding: 20px;
      border-radius: 12px;
      z-index: 10003;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      font-size: 16px;
      text-align: center;
      max-width: 90%;
      border: 2px solid #ff4500;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    `;
    
    tipElement.innerHTML = `
      <div style="margin-bottom: 15px;">⚠️</div>
      <div style="font-weight: bold; margin-bottom: 10px;">Selection Too Small</div>
      <div style="margin-bottom: 15px;">Please select a larger area (minimum ${minSize}×${minSize} pixels)</div>
      <div style="font-size: 14px; color: #ffcccb; margin-bottom: 15px;">
        💡 Try selecting the entire chart area including price axis and time axis
      </div>
      <button style="
        background: #ffd700;
        color: #000;
        border: none;
        padding: 10px 20px;
        border-radius: 6px;
        font-weight: bold;
        cursor: pointer;
        font-size: 14px;
      ">OK</button>
    `;
    
    const safeRemove = () => {
      if (tipElement.parentNode && document.body.contains(tipElement)) {
        document.body.removeChild(tipElement);
      }
    };
    
    tipElement.addEventListener('click', safeRemove);
    
    document.body.appendChild(tipElement);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      safeRemove();
    }, 5000);
  }
}

export const aiAnalysisService = new AIAnalysisService();
export default aiAnalysisService; 